/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { IStackItemProps, IStackProps, Stack, StackItem } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import * as React from 'react';
import { LuisSeparator } from 'src/core/components/LuisSeparator';
import { getStylist } from 'src/core/themes/luisStylist';

export type CardProps = {
    /**
     * The card title.
     */
    title: string;

    /**
     * The content of the card.
     */
    children?: React.ReactNode;
};

const { styleComponent } = getStylist('Card');

const Title = styleComponent<ITextProps>(Text)('Title', theme => ({
    padding: theme.padding,
    fontWeight: theme.fontWeight as Property.FontWeight
}));

const CardContent = styleComponent<IStackItemProps>(StackItem)('CardContent', theme => ({ padding: theme.padding }));

const StyledCard = styleComponent<IStackProps>(Stack)('StyledCard', theme => ({
    boxShadow: theme.boxShadow,
    minWidth: theme.minCardWidth,
    backgroundColor: theme.cardColor
}));

/**
 * The crd component to represent titled card and separates the title and the content with a horizontal separator.
 *
 * @param props The properties of the card component.
 */
export const Card = (props: CardProps) => {
    const { title, children } = props;

    return (
        <StyledCard verticalFill>
            <Title variant="mediumPlus">{title}</Title>
            <LuisSeparator />
            <CardContent grow={1}>{children}</CardContent>
        </StyledCard>
    );
};
